import { OutboundSystemView } from "@redotech/redo-model/views/default-outbound-views";
import { viewTypes } from "@redotech/redo-model/views/views";
import { sinkPromise } from "@redotech/util/promise";
import { memo, useEffect, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import {
  CheckboxValue,
  RedoCheckbox,
} from "./arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { Flex } from "./flex";
import { DisabledTooltip } from "./tooltip/disabled-tooltip";

export const TableFilterEditorHeader = memo(function TableFilterEditorHeader({
  reservedViewNames,
  activeView,
  cancel,
  save,
  savePending,
  viewNotificationsEnabled,
}: {
  reservedViewNames: Set<string>;
  activeView: viewTypes[keyof viewTypes];
  cancel: () => void;
  save: (body: {
    name: string;
    showNotification?: boolean;
    hideWhenEmpty?: boolean;
  }) => Promise<void>;
  savePending: boolean;
  viewNotificationsEnabled?: boolean;
}) {
  const [viewName, setViewName] = useState<string>("");
  const [showNotification, setShowNotification] =
    useState<CheckboxValue>(false);
  const [hideWhenEmpty, setHideWhenEmpty] = useState<CheckboxValue>(false);

  useEffect(() => {
    if (activeView && activeView.name !== OutboundSystemView.CREATE) {
      setViewName(activeView.name);
      if (viewNotificationsEnabled) {
        setShowNotification(
          (activeView as viewTypes["returns"]).showNotification,
        );
        setHideWhenEmpty(
          (activeView as viewTypes["returns"]).hideWhenEmpty ?? false,
        );
      }
    }
  }, [activeView, viewNotificationsEnabled]);

  const submitDisabledBecauseOfEmptyName =
    viewName.length === 0 ? "You must name this view" : false;

  const submitDisabledBecauseOfDuplicateName = reservedViewNames.has(
    viewName.toLowerCase(),
  )
    ? "A view with this name already exists"
    : false;

  const submitDisabled =
    submitDisabledBecauseOfEmptyName || submitDisabledBecauseOfDuplicateName;

  return (
    <Flex align="center" justify="space-between" px="3xl" py="3xl">
      <Flex align="center" gap="xl">
        <RedoTextInput
          placeholder="Enter a name"
          setValue={setViewName}
          value={viewName}
        />
        {viewNotificationsEnabled && (
          <>
            <RedoCheckbox
              label="Enable notifications"
              setValue={setShowNotification}
              size="sm"
              value={showNotification}
            />
            <RedoCheckbox
              label="Hide when empty"
              setValue={(value) => {
                setHideWhenEmpty(value);
              }}
              size="sm"
              value={hideWhenEmpty}
            />
          </>
        )}
      </Flex>
      <Flex align="center">
        <RedoButton hierarchy="secondary" onClick={cancel} text="Cancel" />
        <DisabledTooltip disabledMessage={submitDisabled}>
          <RedoButton
            dataTestId="save-view-button"
            disabled={!!submitDisabled}
            hierarchy="primary"
            onClick={() => {
              sinkPromise(
                save({
                  name: viewName,
                  showNotification:
                    viewNotificationsEnabled &&
                    showNotification.valueOf() === true,
                  hideWhenEmpty:
                    viewNotificationsEnabled &&
                    hideWhenEmpty.valueOf() === true,
                }),
              );
            }}
            pending={savePending}
            text="Save view"
          />
        </DisabledTooltip>
      </Flex>
    </Flex>
  );
});
